import { BASE_URL } from "../../Api/ApiUrls";

/**
 * Property upload permission check (Rent)
 *
 * posted_by values:
 *   - Agent
 *   - Owner
 *   - Builder
 *
 * subscription user_type:
 *   - Agent            → Agent only
 *   - Individual Owner/Builder → Owner + Builder
 *   - Landlord         → Owner + Builder (Rent)
 */
export const checkPropertyUploadLimit_M = async (userId) => {
  try {
    /* --------------------------------
       1. FETCH USER SUBSCRIPTIONS
    -------------------------------- */
    const subRes = await fetch(`${BASE_URL}/Get-user-plan/${userId}/`);
    if (!subRes.ok) {
      return { allowed: false, reason: "NO_SUBSCRIPTION" };
    }

    const subscriptions = await subRes.json();
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
      return { allowed: false, reason: "NO_SUBSCRIPTION" };
    }

    /* --------------------------------
       2. FETCH USER PROPERTIES
    -------------------------------- */
    const propRes = await fetch(`${BASE_URL}/get-property/${userId}/`);
    if (!propRes.ok) {
      return { allowed: false, reason: "ERROR" };
    }

    const properties = await propRes.json();

    /* --------------------------------
       3. COUNT PROPERTIES BY posted_by
    -------------------------------- */
    const counts = { Agent: 0, Owner: 0, Builder: 0 };

    properties.forEach((p) => {
      if (counts[p.posted_by] !== undefined) {
        counts[p.posted_by]++;
      }
    });

    /* --------------------------------
       4. AGGREGATE LIMITS
    -------------------------------- */
    let agentLimit = 0;
    let ownerBuilderLimit = 0;
    let agentUnlimited = false;
    let ownerBuilderUnlimited = false;

    subscriptions.forEach((plan) => {
      // Agent plan
      if (plan.user_type === "Agent") {
        if (plan.no_of_properties_unlimited) {
          agentUnlimited = true;
        } else {
          agentLimit += plan.no_of_properties || 0;
        }
      }

      // Owner + Builder (Individual Owner/Builder OR Landlord)
      if (
        plan.user_type === "Individual Owner/Builder" ||
        plan.user_type === "Landlord"
      ) {
        if (plan.no_of_properties_unlimited) {
          ownerBuilderUnlimited = true;
        } else {
          ownerBuilderLimit += plan.no_of_properties || 0;
        }
      }
    });

    const usedOwnerBuilder = counts.Owner + counts.Builder;

    /* --------------------------------
       5. FINAL AVAILABILITY
    -------------------------------- */
    const availability = {
      Agent: agentUnlimited
        ? { allowed: true, remaining: "Unlimited" }
        : {
            allowed: counts.Agent < agentLimit,
            remaining: Math.max(agentLimit - counts.Agent, 0),
          },

      Owner: ownerBuilderUnlimited
        ? { allowed: true, remaining: "Unlimited" }
        : {
            allowed: usedOwnerBuilder < ownerBuilderLimit,
            remaining: Math.max(ownerBuilderLimit - usedOwnerBuilder, 0),
          },

      Builder: ownerBuilderUnlimited
        ? { allowed: true, remaining: "Unlimited" }
        : {
            allowed: usedOwnerBuilder < ownerBuilderLimit,
            remaining: Math.max(ownerBuilderLimit - usedOwnerBuilder, 0),
          },
    };

    const anyAllowed = Object.values(availability).some(v => v.allowed);

    return {
      allowed: anyAllowed,
      availability,
      reason: anyAllowed ? null : "LIMIT_EXCEEDED",
    };

  } catch (error) {
    console.error("checkPropertyUploadLimit error:", error);
    return { allowed: false, reason: "ERROR" };
  }
};
